import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop } from 'react-image-crop';
import { Modal, Button } from 'react-bootstrap';
import Compressor from 'compressorjs';

import { canvasPreview } from './CanvasPreview';
import { IMAGE_SIZE_COMPRESS_LIMIT } from '../../../config';

const data = {
  buck: { title: 'RWA Ticket' },
  bundle: { aspect: 0.6, dimension: ' ' },
  starConnect: {
    aspect: 0.875,
    title: 'BrandConnect NFT',
    dimension: '(700px x 800px)'
  },
  snft_main: {
    aspect: 1,
    title: 'Campaign',
    dimension: '(1000px x 1000px)'
  },
  snft_marketplace: {
    aspect: 1.23,
    title: 'Marketplace Thumbnail',
    dimension: '(860px x 700px)'
  },
  snft_accesslevel: {
    aspect: 1.74,
    title: 'Package',
    dimension: '(495px x 354px)'
  },
  snft_banner1: { aspect: 1.8, title: 'Banner', dimension: '(1440px x 798px)' },
  snft_banner2: {
    aspect: 1.77,
    title: 'Thumbnail',
    dimension: '(1280px x 720px)'
  },
  snft_banner3: {
    aspect: 1.36,
    title: 'Thumbnail',
    dimension: '(598px x 440px)'
  },
  snft_cover: { aspect: 3.84, title: 'Cover', dimension: '1920px x 500px' },
  snft_welcome_msg: {
    aspect: 1.77,
    title: 'Header Cover',
    dimension: '(1600 x 900px)'
  },
  promotion: { aspect: 1.01, dimension: '(370px x 375px)' },
  accessNft_collection: { title: 'AccessNFT collection' },
  accessNft: {
    aspect: 0.75,
    title: 'AccessNFT',
    dimension: '(1200px x 1600px)'
  },
  profilePicture: {
    aspect: 1,
    title: 'Profile Picture',
    dimension: ' '
  },
  starprofile_header: {
    aspect: 0.973,
    dimension: '*Image size 1600x1800px or 8:9'
  }
};

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 100 }, aspect, mediaWidth, mediaHeight),
    mediaWidth,
    mediaHeight
  );
}

const CropModal = (props) => {
  const {
    src = {},
    showCropModal = false,
    closeModal = () => { },
    handleSave = () => { },
    type = 'buck'
  } = props;

  const [originalImageFile, setOriginalImageFile] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 50, x: 25, y: 25 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [width, setWidth] = useState('30vh');
  const aspect = data[type]?.aspect || 0.5;
  const canvas = useRef();
  const imageRef = useRef();

  const handleSaveImage = () => {
    if (completedCrop?.width && completedCrop?.height) {
      canvas.current.toBlob((blob) => {
        if (!blob) return;

        const fileName = originalImageFile?.name || originalImageFile?.path || 'cropped.jpg';

        new Compressor(blob, {
          quality: type.includes('_banner')
            ? 0.8
            : blob.size > IMAGE_SIZE_COMPRESS_LIMIT
              ? 0.2
              : 0.4,
          success: (result) => {
            const file = new File([result], fileName, { type: result.type });
            if (originalImageFile?.path) file.path = originalImageFile.path;
            handleSave(file);
          }
        });
      });
    } else {
      closeModal();
    }
  };

  useEffect(() => {
    if (showCropModal) {
      if (src) {
        setOriginalImageFile(src);
      } else {
        setOriginalImageFile(null);
        setCrop({ unit: '%', width: 50, x: 25, y: 25 });
        setCompletedCrop(null);
      }
    }
  }, [src, showCropModal]);

  useEffect(() => {
    if (
      completedCrop?.width &&
      completedCrop?.height &&
      imageRef.current &&
      canvas.current
    ) {
      const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
      const scaleY = imageRef.current.naturalHeight / imageRef.current.height;

      const ctx = canvas.current.getContext('2d');
      const pixelRatio = window.devicePixelRatio || 1;

      canvas.current.width = completedCrop.width * pixelRatio;
      canvas.current.height = completedCrop.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(
        imageRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width,
        completedCrop.height
      );
    }
  }, [completedCrop]);


  return (
    <>
      <Modal
        centered
        className="crop-modal"
        show={showCropModal}
        onHide={() => closeModal()}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h4>Please adjust your cropping window slightly</h4>
            <p className="text-white mb-0 mt-2 opacity-75">
              Please crop your image to fit{' '}
              <span className="text-capitalize">
                {data[type]?.title || type}
              </span>{' '}
              image proportions. {data[type]?.dimension || '(600px x 1200px)'}
            </p>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column">
          <ReactCrop
            circularCrop={type === 'profilePicture'}
            aspect={aspect}
            crop={crop}
            onComplete={(c) => setCompletedCrop(c)}
            onChange={(c) => setCrop(c)}
            style={{ maxHeight: '70vh', width: width }}
            ruleOfThirds
          >
            <div style={{ height: '100%', width: '100%' }}>
              <img
                ref={imageRef}
                src={originalImageFile?.preview || originalImageFile || ''}
                onLoad={(e) => {
                  const img = e.currentTarget;
                  const { width, height } = img;

                  const aspectCrop = centerAspectCrop(width, height, aspect);
                  const pixelCrop = convertToPixelCrop(aspectCrop, width, height);

                  if (width > height) setWidth('100%');
                  else setWidth('30vh');

                  setCrop(aspectCrop);
                  setCompletedCrop(pixelCrop);
                }}
                height="100%"
                width="100%"
                alt="cropper"
              />
            </div>
          </ReactCrop>
          <Button
            disabled={!completedCrop?.width || !completedCrop?.height}
            onClick={handleSaveImage}
            className="mt-4 w-100"
            variant="outline-primary"
            size="md"
          >
            Crop Image
          </Button>
        </Modal.Body>
      </Modal>
      <canvas
        ref={canvas}
        style={{
          display: 'none',
          objectFit: 'contain',
          width: completedCrop?.width || 100,
          height: completedCrop?.height || 100
        }}
      />
    </>
  );
};

export default CropModal;
